import React, { useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';

export interface EmailTemplateEditorHandle {
  saveDesign: () => Promise<{ design: object; html: string }>;
  loadDesign: (designJson: object) => void;
}

interface EmailTemplateEditorProps {
  /** Called when the Unlayer editor has finished loading */
  onReady?: () => void;
  /** Minimum height for the editor */
  minHeight?: string;
}

/**
 * Wrapper around Unlayer's EmailEditor that exposes
 * saveDesign() and loadDesign() through a ref handle.
 *
 * Merge tags are configured so tenants can insert {{placeholders}}
 * that get replaced server-side by EmailTemplateRenderer.
 */
const EmailTemplateEditor = forwardRef<EmailTemplateEditorHandle, EmailTemplateEditorProps>(
  ({ onReady, minHeight = '700px' }, ref) => {
    const emailEditorRef = useRef<EditorRef>(null);

    const handleReady: EmailEditorProps['onReady'] = useCallback(
      () => {
        onReady?.();
      },
      [onReady]
    );

    useImperativeHandle(ref, () => ({
      saveDesign: () =>
        new Promise((resolve) => {
          const unlayer = emailEditorRef.current?.editor;
          if (!unlayer) {
            resolve({ design: {}, html: '' });
            return;
          }
          unlayer.exportHtml((data: { design: object; html: string }) => {
            resolve({ design: data.design, html: data.html });
          });
        }),

      loadDesign: (designJson: object) => {
        const unlayer = emailEditorRef.current?.editor;
        if (unlayer && designJson && Object.keys(designJson).length > 0) {
          unlayer.loadDesign(designJson as any);
        }
      },
    }));

    return (
      <EmailEditor
        ref={emailEditorRef}
        onReady={handleReady}
        minHeight={minHeight}
        options={{
          displayMode: 'email',
          version: 'latest',
          locale: 'nl-NL',
          features: {
            textEditor: {
              spellChecker: true,
            },
          },
          mergeTags: {
            relation_name: { name: 'Relatienaam', value: '{{relation.name}}' },
            relation_contactPerson: { name: 'Contactpersoon', value: '{{relation.contactPerson}}' },
            relation_email: { name: 'Relatie e-mail', value: '{{relation.email}}' },
            relation_company: { name: 'Bedrijfsnaam relatie', value: '{{relation.company}}' },
            relation_address: { name: 'Adres', value: '{{relation.address}}' },
            relation_city: { name: 'Plaats', value: '{{relation.city}}' },
            relation_zipCode: { name: 'Postcode', value: '{{relation.zipCode}}' },
            relation_telephoneNumber: { name: 'Telefoonnummer', value: '{{relation.telephoneNumber}}' },
            company_name: { name: 'Bedrijfsnaam', value: '{{company.name}}' },
            date_today: { name: 'Datum vandaag', value: '{{date.today}}' },
            contract_type: { name: 'Contract type', value: '{{contract.type}}' },
            contract_startDate: { name: 'Contract startdatum', value: '{{contract.startDate}}' },
            report_maintenanceDate: { name: 'Onderhoudsdatum', value: '{{report.maintenanceDate}}' },
            report_remark: { name: 'Opmerking', value: '{{report.remark}}' },
            report_doneByName: { name: 'Uitgevoerd door', value: '{{report.doneByName}}' },
            report_moreWorkNeeded: { name: 'Meer werk nodig', value: '{{report.moreWorkNeeded}}' },
            workOrder_date: { name: 'Werkbon datum', value: '{{workOrder.date}}' },
            workOrder_performedTasks: { name: 'Uitgevoerde taken', value: '{{workOrder.performedTasks}}' },
            workOrder_workedHours: { name: 'Gewerkte uren', value: '{{workOrder.workedHours}}' },
          },
        }}
      />
    );
  }
);

EmailTemplateEditor.displayName = 'EmailTemplateEditor';
export { EmailTemplateEditor };
